/* eslint-disable no-unused-vars */
/* eslint-disable max-lines -- V2StepTool encapsulates two modal pickers
   (connection + opcode) plus the cascading editor; keeping it as one
   component matches the V2StepLLM / V2StepChoose pattern where step
   editors own their own modal markup. Splitting purely to satisfy the
   line cap would fragment a coherent UI unit. */
import {
 useContext, useEffect, useMemo, useState
} from 'react';
import {
 mcpDiscoveryService
} from 'shared/api/services/BEX/mcpDiscovery.service';
import type {
 MCPDiscoveredTool
} from 'shared/api/services/bexService.types';
import {
 Label
} from 'shared/components/ui/label';
import {
 Button
} from 'shared/components/ui/button';
import {
 Input
} from 'shared/components/ui/input';
import {
 Badge
} from 'shared/components/ui/badge';
import {
 Card, CardContent
} from 'shared/components/ui/card';
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from 'shared/components/ui/select';
import {
    Dialog, DialogContent, DialogHeader, DialogTitle,
} from 'shared/components/ui/dialog';
import {
 ScrollArea
} from 'shared/components/ui/scroll-area';

import {
 V2OpCodeBuilderContext
} from '../V2OpCodeBuilderContext';
import OELCodeEditor, {
 OELContext
} from '../components/OELCodeEditor';
import StepControllers from '../components/StepControllers';
import {
 ToolStep, RedisJSONOpcode, TypedInput
} from 'builders/builderOpcode/jsonMode/types';
import {
 ToolProtocol
} from 'shared/api/services/bexService.types';
import McpConnectionPicker from 'builders/destudio/opcode-editor/inline-pickers/McpConnectionPicker';
import LongTextField from 'builders/destudio/primitives/LongTextField';

/**
 * V2StepTool — graphical editor for opcode `tool` steps (A-0008.06).
 *
 * Cascading selection (protocol-conditional shape per Option 1 refactor):
 *   1. Protocol — opcode_as_tool (V1) | mcp (A-0008.07; disabled).
 *   2. Connection — only for protocols that need one (mcp). Hidden for
 *      opcode_as_tool (no configurable access boundary).
 *   3. Tool reference:
 *      - opcode_as_tool: opcode picker modal (lists opcodes in same sub-org);
 *        on select, auto-populates step.inputs map from the selected opcode's
 *        TypedInput[] declaration so the builder doesn't type parameter names.
 *      - mcp: free-text tool_name (will be discovery-driven in A-0008.07).
 *   4. Inputs — OEL map of tool-parameter-name -> OEL expression. Names are
 *      pre-filled from the selected opcode for opcode_as_tool; the builder
 *      only fills in the OEL value side.
 *   5. Output schema — optional JSON-Schema fragment.
 */
const V2StepTool = ({
    step,
    onStepChange,
    oelContext,
}: {
    step: ToolStep;
    onStepChange: (step: ToolStep) => void;
    oelContext: OELContext;
}) => {
    const {
        mcpConnections,
        orgId,
        subOrgId,
        opCodes,
        isOpcodeLoading,
    } = useContext(V2OpCodeBuilderContext);

    const [connectionModalOpen, setConnectionModalOpen] = useState(false);
    const [opcodeModalOpen, setOpcodeModalOpen] = useState(false);
    const [opcodeSearch, setOpcodeSearch] = useState('');
    const [searchTerm, setSearchTerm] = useState('');
    const [outputSchemaText, setOutputSchemaText] = useState<string>(
        step.output_schema ? JSON.stringify(step.output_schema, null, 2) : '',
    );
    const [outputSchemaError, setOutputSchemaError] = useState<string>('');

    const protocol = step.protocol || 'opcode_as_tool';
    // Option 1 refactor: protocol-conditional tool_ref shape.
    //   opcode_as_tool: tool_ref = { tool_name }
    //   mcp:           tool_ref = { connection_id, tool_name }
    const requiresConnection = protocol === 'mcp';
    const refAny = (step.tool_ref || {}) as { connection_id?: string; tool_name?: string };
    const connectionId = refAny.connection_id || '';
    const toolName = refAny.tool_name || '';

    // A-0008.07: per-protocol L1 entities mean mcpConnections is already
    // MCP-only — no protocol filter needed. Future protocols will have their
    // own context arrays + their own builders.
    const protocolConnections = protocol === 'mcp' ? (mcpConnections || []) : [];

    // A-0008.07: live tool-name discovery when MCP connection selected.
    const [discoveredTools, setDiscoveredTools] = useState<MCPDiscoveredTool[]>([]);
    const [discoveryError, setDiscoveryError] = useState<string>('');
    const [isDiscovering, setIsDiscovering] = useState(false);

    useEffect(() => {
        if (protocol !== 'mcp' || !connectionId || !orgId || !subOrgId) {
            setDiscoveredTools([]);
            setDiscoveryError('');
            return;
        }
        let cancelled = false;
        setIsDiscovering(true);
        setDiscoveryError('');
        mcpDiscoveryService
            .discover({
 org_id: orgId,
sub_org_id: subOrgId,
connection_id: connectionId
})
            .then((res) => {
                if (cancelled) return;
                setDiscoveredTools(res.tools || []);
            })
            .catch((err) => {
                if (cancelled) return;
                setDiscoveredTools([]);
                setDiscoveryError(err?.message || String(err));
            })
            .finally(() => {
                if (!cancelled) setIsDiscovering(false);
            });
        return () => { cancelled = true; };
    }, [protocol, connectionId, orgId, subOrgId]);

    // When the user picks a tool, auto-populate inputs from its inputSchema
    // AND propagate outputSchema to step.output_schema so downstream steps
    // can reference typed outputs of this MCP tool.
    const handleMCPToolSelect = (newName: string) => {
        const tool = discoveredTools.find((t) => t.name === newName);
        const props = (tool?.inputSchema && typeof tool.inputSchema === 'object'
            ? (tool.inputSchema as any).properties
            : null) || {};
        const next: { [k: string]: string } = {};
        for (const key of Object.keys(props)) {
            next[key] = ((step.inputs || {}) as any)[key] || '';
        }
        const nextStep: ToolStep = {
            ...step,
            tool_ref: {
 connection_id: connectionId,
tool_name: newName
} as any,
            inputs: next,
        };
        if (tool?.outputSchema && typeof tool.outputSchema === 'object') {
            (nextStep as any).output_schema = tool.outputSchema;
        } else {
            // Tool exposes no output schema — clear any prior one so the form
            // doesn't keep stale info.
            delete (nextStep as any).output_schema;
        }
        onStepChange(nextStep);
        // Keep the textarea in sync with the auto-populated schema.
        setOutputSchemaText(
            (nextStep as any).output_schema
                ? JSON.stringify((nextStep as any).output_schema, null, 2)
                : '',
        );
    };

    // Resolve the selected opcode (opcode_as_tool protocol) -- match by opcode_id.
    const selectedOpcode = useMemo<RedisJSONOpcode | undefined>(
        () => (opCodes || []).find((o: RedisJSONOpcode) => (o.opcode_id || (o as any).id) === toolName),
        [opCodes, toolName],
    );

    // Selected opcode's typed input declarations (TypedInput[] -- legacy
    // string[] form is rejected by Server Joi but we tolerate it on read).
    const declaredInputs: TypedInput[] = useMemo(() => {
        if (!selectedOpcode || !selectedOpcode.inputs) return [];
        const arr: any[] = selectedOpcode.inputs as any[];
        return arr.map((entry) => {
            if (typeof entry === 'string') {
                return {
 name: entry,
type: 'string',
required: true
} as TypedInput;
            }
            return entry as TypedInput;
        });
    }, [selectedOpcode]);

    const handleProtocolChange = (newProtocol: ToolProtocol) => {
        const newRef = newProtocol === 'mcp'
            ? {
 connection_id: '',
tool_name: ''
}
            : {
 tool_name: ''
};
        onStepChange({
            ...step,
            protocol: newProtocol,
            tool_ref: newRef as any,
            inputs: {}, // clear inputs map — different protocols expose different params
        });
    };

    const handleConnectionSelect = (newConnectionId: string) => {
        onStepChange({
            ...step,
            tool_ref: {
 ...refAny,
connection_id: newConnectionId,
tool_name: toolName
} as any,
        });
        setConnectionModalOpen(false);
    };

    /**
     * Pick an opcode (opcode_as_tool only). Auto-populates step.inputs map
     * from the selected opcode's declared TypedInput[] -- preserving any OEL
     * values the builder already typed for matching keys, dropping keys that
     * don't apply to the new opcode, and adding empty entries for new keys.
     * Also propagates the opcode's outputs[] (TypedOutput) into the step's
     * output_schema so downstream steps can reference typed outputs.
     */
    const handleOpcodeSelect = (op: RedisJSONOpcode) => {
        const newToolName = op.opcode_id || (op as any).id || '';
        const decl = (op.inputs || []) as any[];
        const declKeys = decl.map((e) => (typeof e === 'string' ? e : e?.name)).filter(Boolean);

        // Carry over any prior OEL values keyed by name; default new entries to ''.
        const prior = step.inputs || {};
        const next: { [k: string]: string } = {};
        for (const key of declKeys) {
            next[key] = (prior as any)[key] || '';
        }

        // Translate opcode.outputs[] (TypedOutput) -> JSON-Schema fragment
        // analogous to MCP's outputSchema. Mirrors Mind's
        // _typed_outputs_to_jsonschema in cpie/tools/opcode_as_tool.py.
        const outDecl = (op.outputs || []) as any[];
        const outProps: Record<string, any> = {};
        for (const entry of outDecl) {
            const name = typeof entry === 'string' ? entry : entry?.name;
            const type = typeof entry === 'string' ? 'string' : (entry?.type || 'string');
            if (!name) continue;
            outProps[name] = {
 type
};
        }
        const outSchema = Object.keys(outProps).length
            ? {
 type: 'object',
properties: outProps
}
            : null;

        const nextStep: ToolStep = {
            ...step,
            tool_ref: {
 tool_name: newToolName
} as any,
            inputs: next,
        };
        if (outSchema) {
            (nextStep as any).output_schema = outSchema;
        } else {
            delete (nextStep as any).output_schema;
        }
        onStepChange(nextStep);
        setOutputSchemaText(outSchema ? JSON.stringify(outSchema, null, 2) : '');
        setOpcodeModalOpen(false);
    };

    const handleToolNameChange = (newName: string) => {
        // mcp path -- builder types the server-exposed tool name freely.
        const newRef: any = requiresConnection
            ? {
 connection_id: connectionId,
tool_name: newName
}
            : {
 tool_name: newName
};
        onStepChange({
 ...step,
tool_ref: newRef
});
    };

    const handleOutputSchemaChange = (text: string) => {
        setOutputSchemaText(text);
        if (!text.trim()) {
            setOutputSchemaError('');
            const next: ToolStep = {
 ...step
};
            delete (next as any).output_schema;
            onStepChange(next);
            return;
        }
        try {
            const parsed = JSON.parse(text);
            setOutputSchemaError('');
            onStepChange({
 ...step,
output_schema: parsed
});
        } catch (err) {
            setOutputSchemaError(err instanceof Error ? err.message : 'invalid JSON');
        }
    };

    const filteredConnections = protocolConnections.filter((c) =>
        c.connection_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        (c.description || '').toLowerCase().includes(searchTerm.toLowerCase()),
    );

    const filteredOpcodes = (opCodes || []).filter((o: RedisJSONOpcode) => {
        const q = opcodeSearch.toLowerCase();
        if (!q) return true;
        return (
            (o.title || '').toLowerCase().includes(q) ||
            (o.opcode_id || '').toLowerCase().includes(q) ||
            (o.description || '').toLowerCase().includes(q)
        );
    });

    const inputEntries = Object.entries(step.inputs || {});
    const isOpcodeAsTool = protocol === 'opcode_as_tool';

    return (
        <div className="space-y-4">
            {/* Protocol */}
            <div className="space-y-1">
                <Label>Protocol <span className="text-destructive">*</span></Label>
                <Select value={protocol} onValueChange={(v) => handleProtocolChange(v as ToolProtocol)}>
                    <SelectTrigger>
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                        <SelectItem value="opcode_as_tool">Opcode as Tool</SelectItem>
                        <SelectItem value="mcp">MCP</SelectItem>
                    </SelectContent>
                </Select>
                {isOpcodeAsTool && (
                    <p className="text-xs text-muted-foreground">
                        Auto-exposes every opcode in this sub-org as a callable tool. Pick one
                        below; its declared inputs will be auto-populated for you to map.
                    </p>
                )}
            </div>

            {/* Connection (only shown for protocols that require one) — chip with Change / Edit / + New popups. */}
            {requiresConnection && (
                <div className="space-y-1">
                    <Label>Connection <span className="text-destructive">*</span></Label>
                    <McpConnectionPicker
                        connectionId={connectionId}
                        onChange={(c: any) => handleConnectionSelect(c?.connection_id || '')}
                    />
                </div>
            )}

            {/* Opcode picker (opcode_as_tool) OR free-text tool name (mcp) */}
            {isOpcodeAsTool ? (
                <div className="space-y-2">
                    <Label>Opcode <span className="text-destructive">*</span></Label>
                    {selectedOpcode ? (
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="secondary" className="font-mono text-sm py-1 px-3">
                                {selectedOpcode.title || selectedOpcode.opcode_id}
                            </Badge>
                            <span className="text-xs text-muted-foreground">
                                ({selectedOpcode.opcode_id})
                            </span>
                            <Button
                                type="button" size="sm" variant="outline"
                                onClick={() => setOpcodeModalOpen(true)}
                            >Change</Button>
                        </div>
                    ) : toolName ? (
                        // tool_name set but no matching opcode in the loaded list
                        // (e.g., opcode was deleted, or list still loading)
                        <div className="flex items-center gap-2 flex-wrap">
                            <Badge variant="destructive" className="font-mono text-sm py-1 px-3">
                                {toolName}
                            </Badge>
                            <span className="text-xs text-destructive">
                                opcode not found in this sub-org
                            </span>
                            <Button
                                type="button" size="sm" variant="outline"
                                onClick={() => setOpcodeModalOpen(true)}
                            >Change</Button>
                        </div>
                    ) : (
                        <Button
                            type="button" variant="outline" className="w-full"
                            onClick={() => setOpcodeModalOpen(true)}
                            disabled={isOpcodeLoading}
                        >
                            {isOpcodeLoading
                                ? 'Loading opcodes...'
                                : (opCodes || []).length === 0
                                    ? 'No opcodes in this sub-org yet — create one first'
                                    : 'Select Opcode'}
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-1">
                    <Label>Tool Name <span className="text-destructive">*</span></Label>
                    {connectionId && discoveredTools.length > 0 ? (
                        <>
                            <Select value={toolName} onValueChange={handleMCPToolSelect}>
                                <SelectTrigger className="w-full">
                                    {toolName ? (
                                        <span className="font-mono text-sm truncate">
                                            {toolName}
                                        </span>
                                    ) : (
                                        <SelectValue placeholder="Pick a tool exposed by the MCP server" />
                                    )}
                                </SelectTrigger>
                                <SelectContent
                                    className="max-w-[var(--radix-select-trigger-width)]"
                                    position="popper"
                                >
                                    {discoveredTools.map((t) => (
                                        <SelectItem key={t.name} value={t.name}>
                                            <div className="flex flex-col w-full min-w-0">
                                                <span className="font-mono text-sm font-semibold truncate">
                                                    {t.name}
                                                </span>
                                                {t.description && (
                                                    <span className="text-xs text-muted-foreground truncate">
                                                        {t.description}
                                                    </span>
                                                )}
                                            </div>
                                        </SelectItem>
                                    ))}
                                </SelectContent>
                            </Select>
                            <p className="text-xs text-muted-foreground">
                                Discovered {discoveredTools.length} tool{discoveredTools.length === 1 ? '' : 's'}
                                {' '}from the MCP server. Selecting one auto-populates inputs and output schema.
                            </p>
                        </>
                    ) : (
                        <>
                            <Input
                                value={toolName}
                                onChange={(e) => handleToolNameChange(e.target.value)}
                                placeholder={
                                    isDiscovering
                                        ? 'Discovering tools…'
                                        : connectionId
                                            ? 'Enter tool name (discovery unavailable)'
                                            : 'Select a connection first'
                                }
                                disabled={!connectionId}
                            />
                            {discoveryError && (
                                <p className="text-xs text-destructive">
                                    Discovery failed: {discoveryError}
                                </p>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* Inputs (tool arguments) — auto-populated for opcode_as_tool */}
            <div className="space-y-2">
                <Label>Inputs (tool arguments)</Label>
                {isOpcodeAsTool && selectedOpcode ? (
                    declaredInputs.length === 0 ? (
                        <p className="text-xs text-muted-foreground">
                            This opcode declares no inputs.
                        </p>
                    ) : (
                        <>
                            <p className="text-xs text-muted-foreground">
                                Pre-filled from the selected opcode's declared inputs. Map each
                                to an OEL expression. Use @input.* / @step_id.output to
                                reference data.
                            </p>
                            <div className="space-y-3">
                                {declaredInputs.map((d) => {
                                    const v = (step.inputs || ({} as any))[d.name] || '';
                                    return (
                                        <div key={d.name} className="space-y-1">
                                            <div className="flex items-center gap-2">
                                                <Badge variant="outline" className="font-mono text-xs">
                                                    {d.name}
                                                </Badge>
                                                <span className="text-xs text-muted-foreground">
                                                    {d.type || 'string'}
                                                    {d.required ? ' • required' : ' • optional'}
                                                    {d.description ? ` — ${d.description}` : ''}
                                                </span>
                                            </div>
                                            <OELCodeEditor
                                                label=""
                                                value={v}
                                                onChange={(val) => {
                                                    const next: any = {
 ...(step.inputs || {})
};
                                                    next[d.name] = val;
                                                    onStepChange({
 ...step,
inputs: next
});
                                                }}
                                                placeholder="OEL expression"
                                                oelContext={oelContext}
                                            />
                                        </div>
                                    );
                                })}
                            </div>
                        </>
                    )
                ) : (
                    <>
                        <p className="text-xs text-muted-foreground">
                            Map each tool parameter name to an OEL expression. Use @input.* /
                            @step_id.output to reference data.
                        </p>
                        <div className="space-y-2">
                            {inputEntries.map(([k, v], idx) => (
                                <div key={`${k}-${idx}`} className="flex items-start gap-2">
                                    <Input
                                        value={k}
                                        placeholder="param name"
                                        className="w-1/3"
                                        onChange={(e) => {
                                            const newKey = e.target.value;
                                            const next = {
 ...(step.inputs || {})
};
                                            delete next[k];
                                            next[newKey] = v as string;
                                            onStepChange({
 ...step,
inputs: next
});
                                        }}
                                    />
                                    <div className="flex-1">
                                        <OELCodeEditor
                                            label=""
                                            value={(v as string) || ''}
                                            onChange={(val) => {
                                                const next = {
 ...(step.inputs || {})
};
                                                next[k] = val;
                                                onStepChange({
 ...step,
inputs: next
});
                                            }}
                                            placeholder="OEL expression"
                                            oelContext={oelContext}
                                        />
                                    </div>
                                    <Button
                                        type="button" size="sm" variant="ghost"
                                        onClick={() => {
                                            const next = {
 ...(step.inputs || {})
};
                                            delete next[k];
                                            onStepChange({
 ...step,
inputs: next
});
                                        }}
                                    >Remove</Button>
                                </div>
                            ))}
                            <Button
                                type="button" size="sm" variant="outline"
                                onClick={() => {
                                    const next = {
 ...(step.inputs || {})
};
                                    const newKey = `param_${Object.keys(next).length + 1}`;
                                    next[newKey] = '';
                                    onStepChange({
 ...step,
inputs: next
});
                                }}
                            >+ Add input</Button>
                        </div>
                    </>
                )}
            </div>

            {/* Output schema (optional, JSON-Schema subset) */}
            <div className="space-y-1">
                <Label>Output Schema (optional)</Label>
                <LongTextField
                    label="Output Schema (JSON-Schema subset)"
                    value={outputSchemaText}
                    onChange={(v) => handleOutputSchemaChange(v)}
                    placeholder='{"type":"object","properties":{...}}'
                    helpText="JSON-Schema subset (type / minimum / maximum / minLength / maxLength / enum). Validated against the tool's output at runtime."
                />
                {outputSchemaError && (
                    <p className="text-xs text-destructive">{outputSchemaError}</p>
                )}
            </div>

            {/* Controllers (when / for / repeat_until) */}
            <StepControllers
                when={step.when}
                forCtrl={step.for}
                repeatUntil={step.repeat_until}
                onWhenChange={(val) => onStepChange({
 ...step,
when: val
})}
                onForChange={(val) => onStepChange({
 ...step,
for: val
})}
                onRepeatUntilChange={(val) => onStepChange({
 ...step,
repeat_until: val
})}
                oelContext={oelContext}
            />

            {/* Connection picker modal (mcp only) */}
            <Dialog open={connectionModalOpen} onOpenChange={setConnectionModalOpen}>
                <DialogContent className="max-w-[800px]">
                    <DialogHeader>
                        <DialogTitle>Select MCP Connection</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            placeholder="Search connections..."
                        />
                        <ScrollArea className="max-h-[400px]">
                            <div className="space-y-2">
                                {filteredConnections.map((c) => (
                                    <Card
                                        key={c.connection_id}
                                        className={`cursor-pointer hover:bg-accent transition-colors ${
                                            connectionId === c.connection_id ? 'border-primary' : ''
                                        }`}
                                        onClick={() => handleConnectionSelect(c.connection_id)}
                                    >
                                        <CardContent className="p-3">
                                            <p className="font-mono font-semibold text-sm">{c.connection_name}</p>
                                            <p className="text-xs text-muted-foreground">
                                                {c.server_url}{c.description ? ` — ${c.description}` : ''}
                                            </p>
                                        </CardContent>
                                    </Card>
                                ))}
                                {filteredConnections.length === 0 && (
                                    <p className="text-center text-sm text-muted-foreground py-4">
                                        No MCP connections yet. Create one in AI Lab → MCP Builder first.
                                    </p>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Opcode picker modal (opcode_as_tool only) */}
            <Dialog open={opcodeModalOpen} onOpenChange={setOpcodeModalOpen}>
                <DialogContent className="max-w-[800px]">
                    <DialogHeader>
                        <DialogTitle>Select Opcode (Tool)</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-3">
                        <Input
                            value={opcodeSearch}
                            onChange={(e) => setOpcodeSearch(e.target.value)}
                            placeholder="Search by title, opcode_id, or description..."
                        />
                        <ScrollArea className="max-h-[400px]">
                            <div className="space-y-2">
                                {filteredOpcodes.map((o: RedisJSONOpcode) => {
                                    const id = o.opcode_id || (o as any).id;
                                    const inputCount = Array.isArray(o.inputs) ? o.inputs.length : 0;
                                    const stepCount = Array.isArray(o.steps) ? o.steps.length : 0;
                                    const isSelected = id === toolName;
                                    return (
                                        <Card
                                            key={id}
                                            className={`cursor-pointer hover:bg-accent transition-colors ${
                                                isSelected ? 'border-primary' : ''
                                            }`}
                                            onClick={() => handleOpcodeSelect(o)}
                                        >
                                            <CardContent className="p-3">
                                                <div className="flex justify-between items-start gap-3">
                                                    <div className="min-w-0">
                                                        <p className="font-semibold text-sm truncate">
                                                            {o.title || id}
                                                        </p>
                                                        <p className="font-mono text-xs text-muted-foreground truncate">
                                                            {id}
                                                        </p>
                                                        {o.description && (
                                                            <p className="text-xs text-muted-foreground mt-1 line-clamp-2">
                                                                {o.description}
                                                            </p>
                                                        )}
                                                    </div>
                                                    <div className="flex flex-col items-end gap-1 shrink-0">
                                                        <Badge variant="outline" className="text-xs">
                                                            {inputCount} input{inputCount === 1 ? '' : 's'}
                                                        </Badge>
                                                        <Badge variant="outline" className="text-xs">
                                                            {stepCount} step{stepCount === 1 ? '' : 's'}
                                                        </Badge>
                                                    </div>
                                                </div>
                                            </CardContent>
                                        </Card>
                                    );
                                })}
                                {filteredOpcodes.length === 0 && (
                                    <p className="text-center text-sm text-muted-foreground py-4">
                                        {(opCodes || []).length === 0
                                            ? 'No opcodes exist in this sub-org yet.'
                                            : 'No opcodes match the search.'}
                                    </p>
                                )}
                            </div>
                        </ScrollArea>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
};

export default V2StepTool;
